import { HouseholdLayout } from '@/components/layouts/HouseholdLayout';
import { PageHeader } from '@/components/PageHeader';
import { useEffect, useState } from 'react';
import { useGetCategoriesQuery, useReorderCategoriesMutation, useReorderSubcategoriesMutation, useUpdateSubcategoryMutation } from '../api';
import { Box, Stack, Card } from '@mui/joy';
import { DndContext, useSensors, useSensor, PointerSensor, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import { useNestedSortable } from '@/hooks/useNestedSortable';
import { CategoryHeader } from '../components/CategoryHeader';
import { Subcategory } from '../components/Subcategory';
import { AddSubcategory } from '../components/AddSubcategory';
import { AddCategory } from '../components/AddCategory';

export function CategoriesPage() {
  const [categories, setCategories] = useState([]);

  const { data, isLoading } = useGetCategoriesQuery({});
  
  useEffect(() => {
    const categories = data?.data || [];
    setCategories(categories.map(cat => ({ 
      ...cat, 
      subcategories: (cat.subcategories || []).map(
        sub => ({ ...sub, id: 'subcategory-' + sub.id, apiId: sub.id })
      ),       
      id: 'category-' + cat.id, 
      apiId: cat.id
    })));    
  }, [data]);

  const handleCategoryCreated = (categoryData) => {
    const newCategory = {
      ...categoryData,
      subcategories: [],
      id: 'category-' + categoryData.id,
      apiId: categoryData.id
    };
    setCategories([...categories, newCategory]);
    
    setTimeout(() => {
      window.scrollTo({ 
        top: document.documentElement.scrollHeight, 
        behavior: 'smooth' 
      });
    }, 100);
  };

  return (
    <HouseholdLayout>
      <Stack direction="column" gap={2}>
        <PageHeader title="Categories" subtitle="Manage ingredient categories and subcategories">      
          <AddCategory onCategoryCreated={handleCategoryCreated} />
        </PageHeader>
        {isLoading ? <Box>Loading...</Box> : <Categories items={categories} setItems={setCategories} />}  
      </Stack>      
    </HouseholdLayout>
  );
}

function Categories({ items, setItems }) {  
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 }}));
  const [reorderCategories] = useReorderCategoriesMutation();
  const [reorderSubcategories] = useReorderSubcategoriesMutation();
  const [updateSubcategory] = useUpdateSubcategoryMutation();

  const handleDragEndWithApi = ({ active, over, items: updatedItems }) => {        
    if (!over) return;    
    
    if (active.isParent) {                  
      if (over.id === active.id) return;
      const orders = updatedItems.map((cat, index) => ({ 
        id: cat.apiId, 
        display_order: index + 1 
      }));
      reorderCategories({ data: { orders } });
      return;
    }

    if (active.parent?.id === over.parent?.id) {        
      if (over.id === active.id) return;
      const orders = over.parent.subcategories.map((sub, index) => ({          
        id: sub.apiId,
        display_order: index + 1
      }));        
      reorderSubcategories({ data: { orders } });        
      return;
    } 
    
    const movedSubcategory = updatedItems
      .flatMap(cat => cat.subcategories || [])
      .find(sub => sub.id === active.id);
        
    if (movedSubcategory && over.parent) {
      updateSubcategory({
        id: movedSubcategory.apiId,
        data: { category_id: over.parent.apiId }
      });
    }
    
    const allOrders = [];
    updatedItems.forEach(category => {
      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach((sub, index) => {
          allOrders.push({
            id: sub.apiId,
            display_order: index + 1
          });
        });
      }
    });
    
    if (allOrders.length > 0) {
      reorderSubcategories({ data: { orders: allOrders } });
    }
  };

  const { 
    isParentItem, 
    activeId, 
    getChildIds, 
    getParentIds,
    ...nestedSortableProps 
  } = useNestedSortable(items, setItems, { subItemsKey: 'subcategories', onDragEnd: handleDragEndWithApi });

  const setCategory = (category) => {
    setItems(items.map(cat => cat.id === category.id ? category : cat));
  };

  const setSubcategory = (categoryId, subcategory) => {
    setItems(items.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          subcategories: cat.subcategories.map(sub => sub.id === subcategory.id ? subcategory : sub)
        };
      }
      return cat;
    }));
  };

  const handleSubcategoryCreated = (categoryId, subcategoryData) => {
    const newSubcategory = {
      ...subcategoryData,
      id: 'subcategory-' + subcategoryData.id,
      apiId: subcategoryData.id
    };

    setItems(items.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          subcategories: [...(cat.subcategories || []), newSubcategory]
        };
      }
      return cat;
    }));
  };

  return (
    <DndContext sensors={sensors} {...nestedSortableProps}>
      <SortableContext items={getParentIds()} strategy={verticalListSortingStrategy}>
        <Stack direction="column" gap={2}>
          {items.map((category) => (
            <SortableCategory 
              key={category.id} 
              category={category}
              getChildIds={getChildIds}              
              setCategory={setCategory}
              setSubcategory={setSubcategory}
              onSubcategoryCreated={(subcategoryData) => handleSubcategoryCreated(category.id, subcategoryData)}
            />
          ))}
        </Stack>
      </SortableContext>
      {createPortal(
        <DragOverlay>
          {activeId ? (
            isParentItem(activeId) ? renderCategoryDragOverlay(activeId, items) : renderSubcategoryDragOverlay(activeId, items)            
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

function renderCategoryDragOverlay(activeId, items) {
  const category = items.find(cat => cat.id === activeId);
  if (!category) return null;
  
  return <Category category={category} dragOverlay />;
}

function renderSubcategoryDragOverlay(activeId, items) {
  for (const category of items) {
    const subcategory = category.subcategories?.find(sub => sub.id === activeId);
    if (subcategory) {
      return <Subcategory subcategory={subcategory} />;
    }
  }
  return null;
}

function SortableCategory({ category, getChildIds, setCategory, setSubcategory, onSubcategoryCreated }) {  
  const [isEditing, setIsEditing] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
    data: { type: 'category' },
    disabled: isEditing,
  });  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(!isEditing ? listeners : {})}>
      <Category 
        category={category} 
        getChildIds={getChildIds} 
        isEditing={isEditing} 
        setIsEditing={setIsEditing} 
        setCategory={setCategory} 
        setSubcategory={setSubcategory}
        onSubcategoryCreated={onSubcategoryCreated}
      />
    </div>
  );
}

function Category({ category, getChildIds, dragOverlay = false, isEditing, setIsEditing, setCategory, setSubcategory, onSubcategoryCreated }) {
  return (
    <Card variant="outlined" sx={{ p: 1 }}>
      <Stack direction="column" gap={1.25}>      
        <CategoryHeader
          category={category}
          isEditing={isEditing}
          onEditStart={() => setIsEditing(true)}
          onEditEnd={(category) => { 
            setIsEditing(false); 
            if (category) {
              setCategory(category);
            }
          }}
        />
        <Subcategories 
          category={category} 
          getChildIds={getChildIds}            
          dragOverlay={dragOverlay}
          setSubcategory={setSubcategory}
          onSubcategoryCreated={onSubcategoryCreated}
        />
      </Stack>
    </Card>
  );
}

function Subcategories({ category, getChildIds, dragOverlay, setSubcategory, onSubcategoryCreated }) {
  return (
    <Stack direction="column" gap={1} sx={{ pl: 2.75 }}>
      {dragOverlay ? (        
        category.subcategories?.map((subcategory) => <Subcategory key={subcategory.id} subcategory={subcategory} />)
      ) : (                
        <SortableContext items={getChildIds(category.id)} strategy={verticalListSortingStrategy}>
          {category.subcategories.map((subcategory) => (
            <SortableSubcategory 
              key={subcategory.id} 
              subcategory={subcategory} 
              categoryId={category.id} 
              setSubcategory={setSubcategory}
            />
          ))}
        </SortableContext>         
      )}
      <AddSubcategory
        categoryId={category.apiId}
        onSubcategoryCreated={onSubcategoryCreated}
      />
    </Stack>
  );
}

function SortableSubcategory({ subcategory, categoryId, setSubcategory }) {
  const [isEditing, setIsEditing] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subcategory.id,
    disabled: isEditing,
  });  

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(!isEditing ? listeners : {})}>
      <Subcategory
        subcategory={subcategory}
        isEditing={isEditing}
        onEditStart={() => setIsEditing(true)}
        onEditEnd={(subcategory) => {
          setIsEditing(false);
          if (subcategory) {
            setSubcategory(categoryId, subcategory);
          }
        }}
      />
    </div>
  );
}
